// CRM Warmup — rehouses the classic WarmupDashboard into the Deliverability shell.
// Network-stats strip + mailbox rail + per-mailbox settings pane (enable, ramp progress,
// numeric settings, behaviour toggles, deliverability score). Card idiom throughout.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      className={'crmwu-toggle' + (checked ? ' crmwu-toggle--on' : '')}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span className="crmwu-toggle-knob" />
    </button>
  );
}

function NumField({ label, value, onChange, min, max, suffix, disabled }) {
  return (
    <div className="crmwu-num">
      <label className="crmwu-num-lbl">{label}</label>
      <div className="crmwu-num-row">
        <input
          type="number"
          className="crmwu-num-input"
          value={value ?? ''}
          min={min}
          max={max}
          disabled={disabled}
          onChange={e => onChange(e.target.value === '' ? null : parseInt(e.target.value))}
        />
        {suffix && <span className="crmwu-num-suffix">{suffix}</span>}
      </div>
    </div>
  );
}

export default function CrmWarmup({ clientId }) {
  const { getToken } = useAuth();
  const [mailboxes, setMailboxes] = useState([]);
  const [network, setNetwork] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchMailboxes = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [mbRes, netRes] = await Promise.all([
        fetch(`${API_BASE}/clients/${clientId}/mailboxes`, { headers }),
        fetch(`${API_BASE}/warmup/network`, { headers }),
      ]);
      if (!mbRes.ok) throw new Error(`Mailboxes failed (${mbRes.status})`);
      const data = await mbRes.json();
      setMailboxes(Array.isArray(data) ? data : data.mailboxes || []);
      if (netRes.ok) setNetwork(await netRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, getToken]);

  useEffect(() => { fetchMailboxes(); }, [fetchMailboxes]);

  const loadSettings = useCallback(async (mb) => {
    setSelectedId(mb.id);
    setSettings(null);
    setLoadingSettings(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/mailboxes/${mb.id}/warmup`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Warmup load failed (${r.status})`);
      setSettings(await r.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingSettings(false);
    }
  }, [getToken]);

  const saveField = useCallback(async (patch) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/mailboxes/${selectedId}/warmup`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      const updated = await r.json();
      setSettings(updated);
      if ('enabled' in patch) {
        setMailboxes(prev => prev.map(m => m.id === selectedId ? { ...m, warmup_enabled: !!patch.enabled } : m));
      }
      toast.success('Saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }, [selectedId, getToken]);

  const selected = mailboxes.find(m => m.id === selectedId) || null;
  const netStats = network ? [
    { label: 'Active in warmup', value: network.total_active || 0 },
    { label: 'Messages today', value: network.messages_today || 0 },
    { label: 'Google', value: network.google || 0 },
    { label: 'Microsoft', value: network.microsoft || 0 },
    { label: 'Zoho', value: network.zoho || 0 },
    { label: 'SMTP', value: network.smtp || 0 },
  ] : [];

  return (
    <div className="crmwu-root">
      {error && <div className="crmwu-error">{error}</div>}

      {network && (
        <div className="crmwu-net">
          {netStats.map(s => (
            <div className="crmwu-net-card" key={s.label}>
              <div className="crmwu-net-lbl">{s.label}</div>
              <div className="crmwu-net-val">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="crmwu-body">
        <div className="crmwu-rail">
          {loading ? (
            <div className="crmwu-rail-empty">Loading…</div>
          ) : mailboxes.length === 0 ? (
            <div className="crmwu-rail-empty">No mailboxes found.</div>
          ) : mailboxes.map(mb => {
            const warm = !!mb.warmup_enabled;
            return (
              <button
                key={mb.id}
                className={'crmwu-rail-item' + (mb.id === selectedId ? ' crmwu-rail-item--on' : '')}
                onClick={() => loadSettings(mb)}
              >
                <div className="crmwu-rail-name">{mb.display_name || mb.email_address}</div>
                <div className="crmwu-rail-email">{mb.email_address}</div>
                <div className={'crmwu-rail-badge' + (warm ? ' crmwu-rail-badge--on' : '')}>
                  {warm ? 'Warming' : 'Off'}
                </div>
              </button>
            );
          })}
        </div>

        <div className="crmwu-pane">
          {!selected ? (
            <div className="crmwu-pane-empty">Select a mailbox to configure its warmup.</div>
          ) : loadingSettings || !settings ? (
            <div className="crmwu-pane-empty">Loading settings…</div>
          ) : (
            <div className="crmwu-detail">
              <div className="crmwu-hero">
                <div className="crmwu-hero-main">
                  <h2 className="crmwu-h2">{selected.email_address}</h2>
                  <div className="crmwu-hero-meta">{settings.enabled ? 'Warmup is active' : 'Warmup is off'}</div>
                </div>
                <div className="crmwu-hero-toggle">
                  <span className="crmwu-hero-toggle-lbl">Enable warmup</span>
                  <Toggle checked={!!settings.enabled} onChange={v => saveField({ enabled: v })} disabled={saving} />
                </div>
              </div>

              <div className="crmwu-card">
                <div className="crmwu-ramp-head">
                  <span>Current daily volume</span>
                  <span>Target</span>
                </div>
                <div className="crmwu-ramp-row">
                  <span className="crmwu-ramp-cur">{settings.daily_volume || 0}</span>
                  <div className="crmwu-ramp-bar">
                    <div className="crmwu-ramp-fill" style={{ width: Math.min(100, ((settings.daily_volume || 0) / Math.max(1, settings.target_volume || 1)) * 100) + '%' }} />
                  </div>
                  <span className="crmwu-ramp-target">{settings.target_volume || 0}/day</span>
                </div>
                {settings.last_ramp_at && (
                  <div className="crmwu-ramp-note">Last ramped {new Date(settings.last_ramp_at).toLocaleDateString('en-GB')}</div>
                )}
              </div>

              <div className="crmwu-card">
                <div className="crmwu-card-title">Volume</div>
                <div className="crmwu-nums">
                  <NumField label="Daily volume" value={settings.daily_volume} min={1} max={500} onChange={v => setSettings({ ...settings, daily_volume: v })} disabled={!settings.enabled || saving} />
                  <NumField label="Target volume" value={settings.target_volume} min={1} max={500} suffix="/day" onChange={v => setSettings({ ...settings, target_volume: v })} disabled={!settings.enabled || saving} />
                  <NumField label="Ramp step" value={settings.ramp_pct} min={1} max={100} suffix="%" onChange={v => setSettings({ ...settings, ramp_pct: v })} disabled={!settings.enabled || saving} />
                  <NumField label="Reply rate" value={settings.reply_rate_pct} min={0} max={100} suffix="%" onChange={v => setSettings({ ...settings, reply_rate_pct: v })} disabled={!settings.enabled || saving} />
                </div>
                <button
                  className="crmwu-btn crmwu-btn--primary crmwu-nums-save"
                  onClick={() => saveField({ daily_volume: settings.daily_volume, target_volume: settings.target_volume, ramp_pct: settings.ramp_pct, reply_rate_pct: settings.reply_rate_pct })}
                  disabled={saving || !settings.enabled}
                >
                  {saving ? 'Saving…' : 'Save volume settings'}
                </button>
              </div>

              <div className="crmwu-card">
                <div className="crmwu-card-title">Behaviour</div>
                <div className="crmwu-beh">
                  <div className="crmwu-beh-row">
                    <div className="crmwu-beh-text">
                      <div className="crmwu-beh-name">Rescue from spam</div>
                      <div className="crmwu-beh-sub">Move warmup messages out of spam to train the inbox classifier.</div>
                    </div>
                    <Toggle checked={!!settings.spam_save_enabled} onChange={v => saveField({ spam_save_enabled: v })} disabled={!settings.enabled || saving} />
                  </div>
                  <div className="crmwu-beh-row">
                    <div className="crmwu-beh-text">
                      <div className="crmwu-beh-name">Mark as important</div>
                      <div className="crmwu-beh-sub">Flag warmup messages as important to reinforce trust signals.</div>
                    </div>
                    <Toggle checked={!!settings.mark_important_enabled} onChange={v => saveField({ mark_important_enabled: v })} disabled={!settings.enabled || saving} />
                  </div>
                </div>
              </div>

              {typeof settings.deliverability_score === 'number' && settings.deliverability_score > 0 && (
                <div className="crmwu-score">
                  Deliverability score <strong>{settings.deliverability_score}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
